import React from 'react'
import { createRoot } from 'react-dom/client'
import CameraViewfinder from './CameraViewfinder'
import { contentColumnOverlaySx } from '../../UI/contentColumn'

/**
 * Prefer the ABR EventFrame shell so the Anear header stays visible. Fall back to
 * document.body only when the uploader is outside an event frame.
 */
export function resolveCameraMountHost(fromElement) {
  if (fromElement?.closest) {
    const frame = fromElement.closest('[data-anear-event-frame]')
    if (frame) return frame
  }
  return typeof document !== 'undefined' ? document.body : null
}

function applyPortalLayout(container, contained) {
  container.className = contained
    ? 'anear-camera-portal anear-camera-portal--frame'
    : 'anear-camera-portal anear-camera-portal--viewport'

  if (contained) {
    Object.assign(container.style, {
      position: 'absolute',
      inset: '0',
      zIndex: '2147483646',
      width: '100%',
      height: '100%',
      pointerEvents: 'auto'
    })
    return
  }

  Object.assign(container.style, {
    position: 'fixed',
    inset: '0',
    zIndex: '2147483646',
    width: '100%',
    height: '100dvh',
    pointerEvents: 'auto',
    ...contentColumnOverlaySx
  })
}

/**
 * Portal the viewfinder into the EventFrame (or body as fallback) and hand back a small
 * handle for pushing prop updates (upload progress) and tearing it down.
 *
 * Capture quality is unchanged — only the preview chrome is scoped to the frame.
 */
export default function mountCameraViewfinder(initialProps = {}) {
  const { mountFrom, ...viewfinderProps } = initialProps
  const host = resolveCameraMountHost(mountFrom)
  if (!host) {
    throw new Error('Camera viewfinder has nowhere to mount.')
  }

  const contained = host !== document.body
  const container = document.createElement('div')
  applyPortalLayout(container, contained)
  host.appendChild(container)

  const root = createRoot(container)
  const commands = { resetToLive: null }
  let props = { ...viewfinderProps, contained, registerCommands: commands }
  let unmounted = false

  const render = () => {
    if (!unmounted) root.render(<CameraViewfinder {...props} />)
  }

  render()

  return {
    update(patch) {
      props = { ...props, ...patch, contained, registerCommands: commands }
      render()
    },
    resetToLive() {
      commands.resetToLive?.()
    },
    unmount() {
      if (unmounted) return
      unmounted = true
      // Unmounting synchronously from inside a React event handler warns, and the close
      // paths are all driven by button clicks.
      window.setTimeout(() => {
        root.unmount()
        container.remove()
      }, 0)
    }
  }
}
